import * as THREE from 'three';

import type { FbxExtractedData } from './extractFbxMaterialColors';
import {
  type FbxMaterialTextureSet,
  type FbxMaterialTextureTile,
  buildUdimAtlasLayout,
} from './fbxUdim';
import type {
  ImportWarningDetail,
  ImportWarningReason,
} from '../io/importWarnings';

const NEAR_BLACK_LUMINANCE_THRESHOLD = 0.04;
const FALLBACK_DIFFUSE_COLOR = new THREE.Color(0.5, 0.5, 0.5);
const MAX_SAFE_UDIM_ATLAS_DIMENSION = 8192;

type MeshStandardTextureProperty =
  | 'map'
  | 'normalMap'
  | 'bumpMap'
  | 'emissiveMap'
  | 'aoMap'
  | 'alphaMap'
  | 'displacementMap'
  | 'metalnessMap'
  | 'roughnessMap';

type DecodedTileImage = {
  source: CanvasImageSource;
  width: number;
  height: number;
  dispose: () => void;
};

type WarningAccumulatorValue = {
  label: string;
  reason: ImportWarningReason;
  contexts: Set<string>;
};

type ConvertedMaterialResult = {
  source: THREE.Material;
  material: THREE.Material;
  standardMaterial: THREE.MeshStandardMaterial | null;
};

const FBX_CHANNEL_TO_MATERIAL_PROPERTY: Partial<
  Record<string, MeshStandardTextureProperty>
> = {
  Bump: 'bumpMap',
  DiffuseColor: 'map',
  'Maya|TEX_color_map': 'map',
  'Maya|TEX_ao_map': 'aoMap',
  DisplacementColor: 'displacementMap',
  EmissiveColor: 'emissiveMap',
  NormalMap: 'normalMap',
  'Maya|TEX_normal_map': 'normalMap',
  SpecularColor: 'metalnessMap',
  TransparentColor: 'alphaMap',
  TransparencyFactor: 'alphaMap',
};

const SRGB_TEXTURE_PROPERTIES = new Set<MeshStandardTextureProperty>([
  'map',
  'emissiveMap',
]);

export interface FbxMaterialCorrectionResult {
  warnings: ImportWarningDetail[];
}

/** sRGB OETF: linear -> sRGB (IEC 61966-2-1). */
function srgbTransferFn(c: number): number {
  if (c <= 0) return 0;
  return c < 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

/**
 * Post-process an FBX scene to fix material issues:
 *
 *  1. Strips texture maps that reference external files (will never load
 *     when the FBX was opened from a blob URL).
 *  2. Converts Phong / Lambert materials to MeshStandardMaterial (PBR).
 *  3. Restores diffuse colors that FBXLoader failed to parse, using colors
 *     extracted directly from the raw FBX data.
 *  4. Applies gamma compensation so FBX viewport colors look correct under PBR.
 *  5. Replaces near-black base colors (no diffuse texture) with a neutral
 *     fallback so the mesh stays visible.
 *
 * Call immediately after FBXLoader resolves, before cloning or adding to scene.
 */
export async function correctFbxMaterials(
  scene: THREE.Group,
  extractedData?: FbxExtractedData,
): Promise<FbxMaterialCorrectionResult> {
  const warningMap = new Map<string, WarningAccumulatorValue>();
  const atlasPromises = new Map<string, Promise<THREE.Texture | null>>();
  const pendingAtlasTasks: Promise<void>[] = [];

  scene.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;

    const materials = Array.isArray(object.material)
      ? object.material
      : [object.material];

    const corrected = materials.map((mat: THREE.Material) => {
      const { source, material, standardMaterial } = convertToStandard(
        mat,
        extractedData,
      );
      if (standardMaterial) {
        pendingAtlasTasks.push(
          applyUdimTextures(
            standardMaterial,
            source,
            extractedData,
            warningMap,
            atlasPromises,
          ).then(() => {
            finalizeStandardMaterial(standardMaterial, source, extractedData);
          }),
        );
      }
      return material;
    });

    object.material = corrected.length === 1 ? corrected[0] : corrected;
  });

  await Promise.all(pendingAtlasTasks);

  return {
    warnings: [...warningMap.values()]
      .map((warning) => ({
        label: warning.label,
        reason: warning.reason,
        contexts: [...warning.contexts].sort(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  };
}

function isNearBlack(color: THREE.Color): boolean {
  const hsl = { h: 0, s: 0, l: 0 };
  // Evaluate in sRGB (perceptual) space so dark-but-visible colors aren't
  // mistaken for black.  Without the color-space argument getHSL operates
  // on linear values which are far smaller and trip the threshold.
  color.getHSL(hsl, THREE.SRGBColorSpace);
  return hsl.l < NEAR_BLACK_LUMINANCE_THRESHOLD;
}

/**
 * Strip FBX name decoration: "\x00\x01Material" suffix (binary) and
 * "TypeName::" prefix (ASCII).
 */
function cleanFbxName(name: string): string {
  return name.replace(/\x00.*$/, '').replace(/^\w+::/, '');
}

function findExtractedColor(
  materialName: string,
  extracted: Map<string, THREE.Color>,
): THREE.Color | null {
  if (!materialName) return null;

  const exact = extracted.get(materialName);
  if (exact) return exact;

  const cleanMat = cleanFbxName(materialName);
  for (const [key, color] of extracted) {
    if (cleanFbxName(key) === cleanMat) return color;
  }

  return null;
}

/** FBXLoader stamps `texture.ID = textureNode.id` on every Texture it creates. */
function isExternalTexture(
  tex: THREE.Texture | null,
  externalIds: Set<number>,
): boolean {
  if (!tex) return false;
  return externalIds.has((tex as unknown as { ID?: number }).ID ?? -1);
}

function stripExternalTextures(
  std: THREE.MeshStandardMaterial,
  externalIds: Set<number>,
): void {
  if (isExternalTexture(std.map, externalIds)) std.map = null;
  if (isExternalTexture(std.normalMap, externalIds)) std.normalMap = null;
  if (isExternalTexture(std.bumpMap, externalIds)) std.bumpMap = null;
  if (isExternalTexture(std.emissiveMap, externalIds)) std.emissiveMap = null;
  if (isExternalTexture(std.aoMap, externalIds)) std.aoMap = null;
  if (isExternalTexture(std.envMap, externalIds)) std.envMap = null;
  if (isExternalTexture(std.alphaMap, externalIds)) std.alphaMap = null;
  if (isExternalTexture(std.displacementMap, externalIds))
    std.displacementMap = null;
  if (isExternalTexture(std.metalnessMap, externalIds)) std.metalnessMap = null;
  if (isExternalTexture(std.roughnessMap, externalIds)) std.roughnessMap = null;
}

function convertToStandard(
  source: THREE.Material,
  extractedData?: FbxExtractedData,
): ConvertedMaterialResult {
  if (
    !(source instanceof THREE.MeshPhongMaterial) &&
    !(source instanceof THREE.MeshLambertMaterial)
  ) {
    if (source instanceof THREE.MeshStandardMaterial) {
      if (extractedData?.externalTextureIds.size) {
        stripExternalTextures(source, extractedData.externalTextureIds);
      }
      return { source, material: source, standardMaterial: source };
    }
    return { source, material: source, standardMaterial: null };
  }

  const std = new THREE.MeshStandardMaterial();

  std.name = source.name;
  std.color.copy(source.color);
  std.map = source.map;
  std.lightMap = source.lightMap;
  std.lightMapIntensity = source.lightMapIntensity;
  std.aoMap = source.aoMap;
  std.aoMapIntensity = source.aoMapIntensity;
  std.emissive.copy(source.emissive);
  std.emissiveMap = source.emissiveMap;
  std.emissiveIntensity = source.emissiveIntensity;
  std.bumpMap = source.bumpMap;
  std.bumpScale = source.bumpScale;
  std.normalMap = source.normalMap;
  if (source.normalScale) {
    std.normalScale.copy(source.normalScale);
  }
  std.displacementMap = source.displacementMap;
  std.displacementScale = source.displacementScale;
  std.displacementBias = source.displacementBias;
  std.alphaMap = source.alphaMap;
  std.envMap = source.envMap;

  std.side = source.side;
  std.transparent = source.transparent;
  std.opacity = source.opacity;
  std.alphaTest = source.alphaTest;
  std.depthWrite = source.depthWrite;
  std.depthTest = source.depthTest;
  std.visible = source.visible;
  std.toneMapped = source.toneMapped;
  std.colorWrite = source.colorWrite;

  if (source instanceof THREE.MeshPhongMaterial) {
    const shininess = source.shininess ?? 30;
    std.roughness = Math.max(0.1, 1.0 - Math.sqrt(shininess / 1000));

    if (source.specular) {
      const specHSL = { h: 0, s: 0, l: 0 };
      source.specular.getHSL(specHSL);
      std.metalness = Math.min(specHSL.l * 0.5, 0.5);
    } else {
      std.metalness = 0.1;
    }

    if (source.specularMap) {
      std.metalnessMap = source.specularMap;
    }
  } else {
    std.roughness = 0.9;
    std.metalness = 0.0;
  }

  // Strip texture maps backed by non-embedded FBX Videos. These reference
  // external files that can't resolve when the FBX is loaded from a blob URL.
  if (extractedData?.externalTextureIds.size) {
    stripExternalTextures(std, extractedData.externalTextureIds);
  }

  source.dispose();
  return { source, material: std, standardMaterial: std };
}

function finalizeStandardMaterial(
  std: THREE.MeshStandardMaterial,
  source: THREE.Material,
  extractedData?: FbxExtractedData,
): void {
  // When a remaining (embedded) texture map has a near-black base color,
  // the mesh renders as black (color * texture = 0). Set to white so the
  // texture shows correctly once loaded.
  if (std.map && isNearBlack(std.color)) {
    std.color.setRGB(1, 1, 1);
  }

  // Patch color from raw FBX data when FBXLoader dropped it (strict type check
  // on DiffuseColor rejects valid "Vector3D" types). When FBXLoader read the
  // color correctly the extracted value is identical, so overwriting is safe.
  if (extractedData?.colors && !std.map) {
    const extracted = findExtractedColor(source.name, extractedData.colors);
    if (extracted) {
      std.color.copy(extracted);
    }
  }

  // FBX viewport shading (Lambert/Phong) is not energy-conserving and uses
  // brighter default lighting than PBR. Reverse the sRGB->linear gamma so
  // the stored color matches the intended visual brightness from the DCC tool.
  if (!std.map) {
    std.color.r = srgbTransferFn(std.color.r);
    std.color.g = srgbTransferFn(std.color.g);
    std.color.b = srgbTransferFn(std.color.b);
  }

  if (isNearBlack(std.color) && !std.map) {
    std.color.copy(FALLBACK_DIFFUSE_COLOR);
  }

  std.needsUpdate = true;
}

async function applyUdimTextures(
  material: THREE.MeshStandardMaterial,
  source: THREE.Material,
  extractedData: FbxExtractedData | undefined,
  warningMap: Map<string, WarningAccumulatorValue>,
  atlasPromises: Map<string, Promise<THREE.Texture | null>>,
): Promise<void> {
  if (!extractedData?.udimTextureSets.length) return;

  const matchingSets = extractedData.udimTextureSets.filter(
    (set) => cleanFbxName(set.materialName) === cleanFbxName(source.name),
  );
  if (matchingSets.length === 0) return;

  for (const set of matchingSets) {
    if (!set.isUdim) continue;

    const materialProperty = FBX_CHANNEL_TO_MATERIAL_PROPERTY[set.channel];
    const label = `${cleanFbxName(set.materialName)} / ${set.channel}`;
    if (!materialProperty) {
      addWarning(
        warningMap,
        label,
        'udimUnsupported',
        collectTextureContexts(set),
      );
      continue;
    }

    const embeddedTiles = set.tiles.filter((tile) => tile.content !== null);
    if (embeddedTiles.length === 0) {
      addWarning(
        warningMap,
        label,
        'udimExternal',
        collectTextureContexts(set),
      );
      continue;
    }

    const unresolvedTiles = embeddedTiles.filter((tile) => tile.tile === null);
    if (unresolvedTiles.length > 0) {
      addWarning(
        warningMap,
        label,
        'udimUnsupported',
        unresolvedTiles.map(
          (tile) => tile.relativeFileName ?? tile.fileName ?? 'embedded tile',
        ),
      );
      continue;
    }

    const cacheKey = `${label}:${embeddedTiles
      .map((tile) => tile.tile)
      .join(',')}`;
    let atlasPromise = atlasPromises.get(cacheKey);
    if (!atlasPromise) {
      atlasPromise = buildAtlasTexture(
        set,
        materialProperty,
        material[materialProperty] as THREE.Texture | null,
        warningMap,
      );
      atlasPromises.set(cacheKey, atlasPromise);
    }

    const atlasTexture = await atlasPromise;
    if (!atlasTexture) continue;

    material[materialProperty] = atlasTexture;
    if (materialProperty === 'alphaMap') {
      material.transparent = true;
    }
    if (materialProperty === 'map' && isNearBlack(material.color)) {
      material.color.setRGB(1, 1, 1);
    }
  }
}

async function buildAtlasTexture(
  set: FbxMaterialTextureSet,
  materialProperty: MeshStandardTextureProperty,
  templateTexture: THREE.Texture | null,
  warningMap: Map<string, WarningAccumulatorValue>,
): Promise<THREE.Texture | null> {
  const label = `${cleanFbxName(set.materialName)} / ${set.channel}`;
  const embeddedTiles = set.tiles.filter(
    (
      tile,
    ): tile is FbxMaterialTextureTile & {
      tile: number;
      content: NonNullable<FbxMaterialTextureTile['content']>;
    } => tile.content !== null && tile.tile !== null,
  );
  if (embeddedTiles.length === 0) return null;

  const layout = buildUdimAtlasLayout(embeddedTiles.map((tile) => tile.tile));
  if (!layout) {
    addWarning(
      warningMap,
      label,
      'udimUnsupported',
      collectTextureContexts(set),
    );
    return null;
  }

  if (layout.missingTiles.length > 0) {
    addWarning(
      warningMap,
      label,
      'udimMissing',
      layout.missingTiles.map((tile) => `tile ${tile}`),
    );
  }

  let decodedTiles: Array<{
    tile: FbxMaterialTextureTile & {
      tile: number;
      content: NonNullable<FbxMaterialTextureTile['content']>;
    };
    decoded: DecodedTileImage;
  }>;
  try {
    decodedTiles = await Promise.all(
      embeddedTiles.map(async (tile) => ({
        tile,
        decoded: await decodeEmbeddedTile(tile),
      })),
    );
  } catch (error) {
    addWarning(warningMap, label, 'udimUnsupported', [
      error instanceof Error
        ? error.message
        : 'Failed to decode one or more embedded UDIM tiles',
    ]);
    return null;
  }
  if (decodedTiles.length === 0) return null;

  const tileWidth = decodedTiles[0].decoded.width;
  const tileHeight = decodedTiles[0].decoded.height;
  const mismatchedTile = decodedTiles.find(
    ({ decoded }) =>
      decoded.width !== tileWidth || decoded.height !== tileHeight,
  );
  if (mismatchedTile) {
    decodedTiles.forEach(({ decoded }) => decoded.dispose());
    addWarning(warningMap, label, 'udimUnsupported', [
      'Embedded UDIM tiles do not share a common resolution',
    ]);
    return null;
  }

  const atlasWidth = tileWidth * layout.columns;
  const atlasHeight = tileHeight * layout.rows;
  if (
    atlasWidth > MAX_SAFE_UDIM_ATLAS_DIMENSION ||
    atlasHeight > MAX_SAFE_UDIM_ATLAS_DIMENSION
  ) {
    decodedTiles.forEach(({ decoded }) => decoded.dispose());
    addWarning(warningMap, label, 'udimTextureLimit', [
      `${atlasWidth}x${atlasHeight} atlas`,
    ]);
    return null;
  }

  if (typeof document === 'undefined') {
    decodedTiles.forEach(({ decoded }) => decoded.dispose());
    addWarning(warningMap, label, 'udimUnsupported', [
      'Canvas rendering is unavailable in the current environment',
    ]);
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = atlasWidth;
  canvas.height = atlasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    decodedTiles.forEach(({ decoded }) => decoded.dispose());
    addWarning(warningMap, label, 'udimUnsupported', [
      'Canvas 2D context unavailable while rebuilding UDIM atlas',
    ]);
    return null;
  }

  for (const { tile, decoded } of decodedTiles) {
    if (tile.u === null || tile.v === null) {
      decoded.dispose();
      continue;
    }

    const column = tile.u - layout.minU;
    const rowFromTop = tile.v - layout.minV;
    ctx.drawImage(
      decoded.source,
      column * tileWidth,
      rowFromTop * tileHeight,
      tileWidth,
      tileHeight,
    );
    decoded.dispose();
  }

  const atlasTexture = new THREE.CanvasTexture(canvas);
  atlasTexture.colorSpace = SRGB_TEXTURE_PROPERTIES.has(materialProperty)
    ? THREE.SRGBColorSpace
    : (templateTexture?.colorSpace ?? THREE.NoColorSpace);
  atlasTexture.wrapS = THREE.RepeatWrapping;
  atlasTexture.wrapT = THREE.RepeatWrapping;
  atlasTexture.repeat.set(layout.repeatX, layout.repeatY);
  atlasTexture.offset.set(layout.offsetX, layout.offsetY);
  atlasTexture.rotation = templateTexture?.rotation ?? 0;
  if (templateTexture?.center) {
    atlasTexture.center.copy(templateTexture.center);
  }
  atlasTexture.magFilter = templateTexture?.magFilter ?? atlasTexture.magFilter;
  atlasTexture.minFilter = templateTexture?.minFilter ?? atlasTexture.minFilter;
  atlasTexture.anisotropy =
    templateTexture?.anisotropy ?? atlasTexture.anisotropy;
  atlasTexture.generateMipmaps =
    templateTexture?.generateMipmaps ?? atlasTexture.generateMipmaps;
  atlasTexture.needsUpdate = true;
  return atlasTexture;
}

async function decodeEmbeddedTile(
  tile: FbxMaterialTextureTile,
): Promise<DecodedTileImage> {
  const mimeType = tile.mimeType ?? 'application/octet-stream';
  const blob =
    tile.content?.kind === 'binary'
      ? new Blob([new Uint8Array(tile.content.bytes)], { type: mimeType })
      : tile.content?.kind === 'base64'
        ? await base64ToBlob(tile.content.data, mimeType)
        : null;
  if (!blob) {
    throw new Error('FBX tile content is missing');
  }

  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(blob);
    return {
      source: bitmap,
      width: bitmap.width,
      height: bitmap.height,
      dispose: () => bitmap.close(),
    };
  }

  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await loadImage(objectUrl);
    return {
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      dispose: () => {},
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function base64ToBlob(data: string, mimeType: string): Promise<Blob> {
  const response = await fetch(`data:${mimeType};base64,${data}`);
  return response.blob();
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error(`Failed to decode embedded FBX texture: ${src}`));
    image.src = src;
  });
}

function collectTextureContexts(set: FbxMaterialTextureSet): string[] {
  return [
    ...new Set(
      set.tiles.map((tile) => tile.relativeFileName ?? tile.fileName ?? ''),
    ),
  ]
    .filter(Boolean)
    .sort();
}

function addWarning(
  warningMap: Map<string, WarningAccumulatorValue>,
  label: string,
  reason: ImportWarningReason,
  contexts: string[],
): void {
  const key = `${label}:${reason}`;
  const existing = warningMap.get(key);
  if (existing) {
    contexts.forEach((context) => existing.contexts.add(context));
    return;
  }

  warningMap.set(key, {
    label,
    reason,
    contexts: new Set(contexts.filter(Boolean)),
  });
}
